<template>
  <div v-if="visible" class="webchat-wrapper">
    <button class="webchat-close-btn" @click="handleClose" type="button">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
    <div v-if="isLoading" class="loading-overlay">
      <moon-loader :loading="true" color="#188bf6" size="20px" />
    </div>
    <div id="webchat" />
  </div>
</template>

<script>
export default {
  name: "BotpressChat",

  props: {
    visible: {
      type: Boolean,
      default: true,
    },
    botId: {
      type: String,
      required: true,
    },
    clientId: {
      type: String,
      required: true,
    },
    visitorId: { type: String, default: "" },
    botName: { type: String, default: "" },
    botAvatar: { type: String, default: "" },
    isAiFirstUser: { type: String, default: "false" },
    daysSincePurchase: { type: String, default: "0" },
    rawPurchaseDate: { type: String, default: "" },
    userRole: { type: String, default: "" },
    userEmail: { type: String, default: "" },
    userLocale: { type: String, default: "" },
    userName: { type: String, default: "" },
    userPermissions: { type: String, default: "" },
    userPhone: { type: String, default: "" },
    agencyActivePlan: { type: String, default: "" },
    agencyCompanyEmail: { type: String, default: "" },
    agencyCompanyId: { type: String, default: "" },
    agencyCompanyStatus: { type: String, default: "" },
    agencyDateCreated: { type: String, default: "" },
    agencyDatePurchased: { type: String, default: "" },
    agencyHipaaEnabled: { type: String, default: "" },
    agencyLocationCount: { type: String, default: "" },
    agencyName: { type: String, default: "" },
    agencyPremiumSupport: { type: String, default: "" },
    agencyPremiumSubscription: { type: String, default: "" },
    agencyRelationshipId: { type: String, default: "" },
    agencyTimezone: { type: String, default: "" },
    agencyStripeCustomerId: { type: String, default: "" },
    agencyWhiteLabelUrl: { type: String, default: "" },
    isEnterprise: { type: String, default: "" },
    currentUrl: { type: String, default: "" },
    locationId: { type: String, default: "" },
    locationName: { type: String, default: "" },
    shouldShowSupportAIForImplemented: { type: Boolean, default: false },
    source: { type: String, default: "" },
  },

  data() {
    return {
      isLoading: true,
      botpressScriptLoaded: false,
      botpressHandlersSetup: false,
    };
  },

  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadBotpressScript();
        } else if (window.botpress) {
          const shouldBeOpen = localStorage.getItem("botpress-webchat-open") === "true";
          if (!shouldBeOpen) {
            window.botpress.close();
          }
        }
      },
    },
  },

  mounted() {
    this.setupEventListeners();
  },

  beforeDestroy() {
    this.removeEventListeners();
    const shouldPreserveWidget = localStorage.getItem("botpress-webchat-open") === "true";
    if (!this.visible && !shouldPreserveWidget) {
      this.destroyBotpress();
    }
  },

  methods: {
    setupEventListeners() {
      window.addEventListener("message", this.handleBotpressMessage);
    },

    removeEventListeners() {
      window.removeEventListener("message", this.handleBotpressMessage);
    },

    loadBotpressScript() {
      if (this.botpressScriptLoaded) {
        this.initializeBotpress();
        return;
      }

      this.isLoading = true;

      const script = document.createElement("script");
      script.src = "https://cdn.botpress.cloud/webchat/v3.6/inject.js";
      script.async = true;
      script.onload = () => {
        this.botpressScriptLoaded = true;
        this.initializeBotpress();
      };

      document.body.appendChild(script);
    },

    initializeBotpress() {
      if (!window.botpress) {
        console.error("Botpress script not loaded properly");
        return;
      }

      this.setupBotpressEventHandlers();

      window.botpress.init({
        botId: this.botId,
        user: {
          data: {
            visitorId: this.visitorId,
            userRole: this.userRole,
            activePlan: this.agencyActivePlan,
            agencyName: this.agencyName,
            companyId: this.agencyCompanyId,
            companyEmail: this.agencyCompanyEmail,
            dateCreated: this.agencyDateCreated,
            datePurchased: this.agencyDatePurchased,
            email: this.userEmail,
            hipaaEnabled: this.agencyHipaaEnabled,
            localeString: this.userLocale,
            locationCount: this.agencyLocationCount,
            locationId: this.locationId,
            locationName: this.locationName,
            name: this.userName,
            phone: this.userPhone,
            premiumUpgraded: this.agencyPremiumSupport,
            premiumUpgradeStatus: this.agencyPremiumSubscription,
            relationshipId: this.agencyRelationshipId,
            status: this.agencyCompanyStatus,
            stripeCustomerId: this.agencyStripeCustomerId,
            timezone: this.agencyTimezone,
            userPermissions: this.userPermissions,
            whiteLabelUrl: this.agencyWhiteLabelUrl,
            currentUrl: this.currentUrl,
            isEnterprise: this.isEnterprise,
            isAiFirstUser: this.isAiFirstUser,
            daysSincePurchase: this.daysSincePurchase,
            rawPurchaseDate: this.rawPurchaseDate,
            showSupportAIForImplement: this.shouldShowSupportAIForImplemented.toString(),
            source: this.source,
          },
        },
        configuration: {
          version: "v2",
          embeddedChatId: "webchat",
          botAvatar: this.botAvatar,
          botName: this.botName,
          website: {},
          email: {},
          phone: {},
          termsOfService: {},
          privacyPolicy: {},
          color: "#004EEB",
          variant: "soft",
          themeMode: "light",
          fontFamily: "inter",
          radius: 1,
          additionalStylesheetUrl: "",
          showCloseButton: true,
          showPoweredBy: false,
          allowFileUpload: true,
          feedbackEnabled: true,
          footer: "",
          composerPlaceholder: "Ask your question here…\n(Shift + Enter for a new line)",
          conversationHistory: true,
        },
        clientId: this.clientId,
      });

      setTimeout(() => {
        if (this.isLoading) {
          this.isLoading = false;
        }
      }, 5000);
    },

    setupBotpressEventHandlers() {
      if (!window.botpress || this.botpressHandlersSetup) return;
      this.botpressHandlersSetup = true;

      window.botpress.on("webchat:ready", this.onWebchatReady);
      window.botpress.on("webchat:opened", this.onWebchatOpened);
      window.botpress.on("customEvent", this.onBotpressCustomEvent);
      window.botpress.on("webchat:closed", this.onWebchatClosed);
    },

    onWebchatReady() {
      this.isLoading = true;
      window.botpress.open();
      setTimeout(() => {
        if (this.isLoading) {
          this.isLoading = false;
          this.injectCustomCSS();
        }
      }, 2000);
    },

    onWebchatOpened() {
      this.isLoading = false;
      this.injectCustomCSS();
      // Retry after messages render in case shadow DOM elements weren't ready
      setTimeout(() => this.injectCustomCSS(), 1500);
    },

    onBotpressCustomEvent(event) {
      if (event.eventType === "thinking") {
        const webchatElement = document.getElementsByName("webchat")[0];
        const webchatDoc = webchatElement?.contentDocument;
        const composerInput = webchatDoc?.getElementsByClassName("bpComposerInput")[0];
        if (composerInput) {
          composerInput.disabled = true;
        }
      } else if (event.eventType === "doneThinking") {
        const webchatElement = document.getElementsByName("webchat")[0];
        const webchatDoc = webchatElement?.contentDocument;
        const composerInput = webchatDoc?.getElementsByClassName("bpComposerInput")[0];
        if (composerInput) {
          composerInput.disabled = false;
        }
      }

      if (event.type === "timeout") {
        window.botpress.close();
        window.parent.postMessage("clearConversation", "*");
        this.clearConversation();
      }

      if (event.type === "implementedUser") {
        window.parent.postMessage({ type: "implementedUser" }, "*");
        setTimeout(() => {
          window.parent.postMessage("closeWebchat", "*");
        }, 3000);
      }

      if (event.type === "openFreshChat") {
        window.parent.postMessage(
          {
            type: "openTicket",
            payload: {
              botType: event.payload?.botType || "default",
              supportPod: event.payload?.supportPod || "",
              topic: event.payload?.topic || "",
              product: event.payload?.product || "",
              question: event.payload?.question || "",
              summary: event.payload?.summary || "",
              supportType: event.payload?.supportType || "",
              viewType: event.payload?.viewType || "",
              transcript: event.payload?.transcript || "",
            },
          },
          "*"
        );
        setTimeout(() => {
          this.clearConversation();
          window.parent.postMessage("closeWebchat", "*");
        }, 5000);
      }
    },

    onWebchatClosed() {
      window.parent.postMessage("closeWebchat", "*");
      this.$emit("update:visible", false);
      localStorage.setItem("botpress-webchat-open", "false");
    },

    handleBotpressMessage(event) {
      if (event.data === "closeWebchat") {
        this.$emit("update:visible", false);
      } else if (event.data === "clearConversation") {
        this.clearConversation();
      } else if (event.data === "botInitialized") {
        this.isLoading = false;
      } else if (event.data === "botInitializedStart") {
        this.isLoading = true;
      } else if (event.data?.type === "openTicket") {
        this.clearConversation();
        window.parent.postMessage(event.data, "*");
        setTimeout(() => {
          this.$emit("update:visible", false);
        }, 5000);
      }
    },

    injectCustomCSS() {
      try {
        const shadowHost = document.querySelector("#webchat .bpEmbeddedWebchat");
        const shadowRoot = shadowHost?.shadowRoot;
        if (!shadowRoot) {
          setTimeout(() => this.injectCustomCSS(), 400);
          return;
        }

        // Remove old style if present so we always inject fresh
        const existing = shadowRoot.querySelector("#bp-custom-style");
        if (existing) existing.remove();

        const style = document.createElement("style");
        style.id = "bp-custom-style";
        style.textContent = `
          .bpHeaderContentActionsContainer {
            margin-right: 38px;
          }
          .bpComposerInput::placeholder {
            font-style: italic !important;
            font-size: 0.700rem !important;
          }
          p,
          p *,
          [class*="essage"] p,
          [class*="essage"] span,
          [class*="essage"] div,
          [class*="ubble"] p,
          [class*="ubble"] span,
          [class*="ubble"] div,
          [class*="content"] p,
          [class*="text"] p {
            text-align: left !important;
          }
        `;
        shadowRoot.appendChild(style);
      } catch (error) {
        console.error("Failed to inject custom CSS:", error);
      }
    },

    clearConversation() {
      localStorage.removeItem(`bp-webchat-${this.clientId}`);
      localStorage.removeItem("botpress-message-history");
      this.$emit("update:visible", false);
      localStorage.setItem("botpress-webchat-open", "false");
    },

    handleClose() {
      this.$emit("update:visible", false);
      localStorage.setItem("botpress-webchat-open", "false");
      if (window.botpress) {
        window.botpress.close();
      }
    },

    destroyBotpress() {
      if (window.botpress) {
        window.botpress.close();
      }
    },
  },
};
</script>

<style scoped>
.webchat-wrapper {
  position: fixed;
  top: var(--topbar-height, 44px);
  left: var(--sidebar-width, 180px);
  right: 1rem;
  height: calc(100vh - var(--topbar-height, 44px) - 1rem);
  z-index: 9999;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 0.375rem;
  overflow: hidden;
}

#webchat {
  height: 100%;
  width: 100%;
}

.webchat-close-btn {
  position: absolute;
  top: 9px;
  right: 10px;
  z-index: 10001;
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5f6276;
  padding: 0;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.webchat-close-btn:hover {
  background-color: rgba(185, 187, 198, 0.2);
  color: #3f414f;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}
</style>
